import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SalesReadingsService } from '../../../../services/sales-readings.service';
import { AuthService } from '../../../../services/auth.service';
import * as moment from 'moment';
import { PdfImageService } from 'src/services/pdf-image.service';

// Constants
const READING_TYPES = {
  // Z_READING: 'z-reading',
  // X_READING: 'x-reading',
  BIR_SUMMARY: 'bir-summary',
  REFUND: 'refund',
  CANCEL: 'cancel',
  VOID: 'void',
  RETURN: 'return',
  SENIOR_CITIZEN: 'senior-citizen',
  PWD: 'pwd',
  NATIONAL_ATHLETES_COACHES: 'national-athletes-coaches',
  SOLO_PARENT: 'solo-parent'
} as const;

const READING_TYPE_NAMES = {
  // [READING_TYPES.Z_READING]: 'Z Reading (End-of-Day Sales Report)',
  // [READING_TYPES.X_READING]: 'X Reading (Interim Sales Report)',
  [READING_TYPES.BIR_SUMMARY]: 'BIR Sales Summary Report',
  [READING_TYPES.REFUND]: 'Refund Transactions Report',
  [READING_TYPES.CANCEL]: 'Cancelled Transactions Report',
  [READING_TYPES.VOID]: 'Void Transactions Report',
  [READING_TYPES.RETURN]: 'Return Transactions Report',
  [READING_TYPES.SENIOR_CITIZEN]: 'Senior Citizen Sales Report',
  [READING_TYPES.PWD]: 'PWD Sales Report',
  [READING_TYPES.NATIONAL_ATHLETES_COACHES]: 'National Athletes & Coaches Sales Report',
  [READING_TYPES.SOLO_PARENT]: 'Solo Parent Sales Report'
} as const;

@Component({
  selector: 'app-card-sales-readings',
  templateUrl: './card-sales-readings.component.html',
  styleUrls: ['./card-sales-readings.component.css']
})
export class CardSalesReadingsComponent implements OnInit {

  @Input() color: string = "light";

  form: FormGroup;
  isLoading = false;
  showResults = false;
  salesReadingData: any = null;
  activeReadingType = '';
  activeQuickDateRange: string = '';
  isSettingQuickRange = false;

  minDate: Date;
  maxDate: Date;

  // Pagination properties
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 1;

  zones: any[] = [];
  employees: any[] = [];
  account: any;

  constructor(
    private fb: FormBuilder,
    private salesReadingsService: SalesReadingsService,
    private auth: AuthService,
    private cdr: ChangeDetectorRef,
    private pdfImageService: PdfImageService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadAccountData();
    this.setupFormChangeListeners();
  }

  private initializeForm(): void {
    const today = moment().toDate();
    this.form = this.fb.group({
      readingType: [READING_TYPES.BIR_SUMMARY, Validators.required],
      zone: ['all'],
      employee: ['all'],
      dateRangeLogsFrom: [today, Validators.required],
      dateRangeLogsTo: [today, Validators.required]
    });

    // Set min and max dates
    this.minDate = moment().subtract(1, 'year').toDate();
    this.maxDate = moment().add(1, 'year').toDate();
  }

  private loadAccountData(): void {
    try {
      const accountData = localStorage.getItem('account');
      if (!accountData) return;

      this.account = JSON.parse(accountData);
      this.loadZones();
      this.loadEmployees();
    } catch (error) {
      console.error('Error loading account data:', error);
    }
  }

  private loadZones(): void {
    this.zones = this.account?.settings?.zones || [];
  }

  private loadEmployees(): void {
    this.auth.user$.subscribe(user => {
      if (!user?.roles?.superAdmin || !this.account?.settings?.zones?.length) {
        return;
      }

      const employeeSet = new Set<string>();
      const employees: any[] = [{
        employee_id: 'all',
        first_name: 'All',
        last_name: '',
        position: '',
        date_created: null
      }];

      this.account.settings.zones.forEach((zone: any) => {
        zone.employees?.forEach((employee: any) => {
          if (!employeeSet.has(employee.employee_id)) {
            employeeSet.add(employee.employee_id);
            employees.push(employee);
          }
        });
      });

      this.employees = employees;
    });
  }

  async generateSalesReading(): Promise<void> {
    if (this.form.invalid) return;

    const { readingType, dateRangeLogsFrom, dateRangeLogsTo, zone, employee } = this.form.value;

    // Validate date range
    if (!this.validateDateRange(dateRangeLogsFrom, dateRangeLogsTo)) {
      return;
    }

    this.activeReadingType = readingType;
    this.isLoading = true;
    this.showResults = false;

    try {
      const start = moment(dateRangeLogsFrom).startOf('day').toDate();
      const end = moment(dateRangeLogsTo).endOf('day').toDate();

      console.log('Sales Reading Filters:', {
        readingType,
        startDate: dateRangeLogsFrom,
        endDate: dateRangeLogsTo,
        zone,
        employee
      });

      const data = await this.getReadingData(readingType, start, end, zone, employee);

      console.log("getReadingData", data);

      this.salesReadingData = data;
      this.updatePagination(); // Initialize pagination
      this.showResults = true;
    } catch (error) {
      console.error('Error generating sales reading:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private setupFormChangeListeners(): void {
    // Subscribe to form changes to clear active state when user manually changes dates
    this.form.get('dateRangeLogsFrom')?.valueChanges.subscribe(() => this.onDateInputChange());
    this.form.get('dateRangeLogsTo')?.valueChanges.subscribe(() => this.onDateInputChange());
  }

  private validateDateRange(startDate: Date | string, endDate: Date | string): boolean {
    if (!startDate || !endDate) {
      return false;
    }

    const start = moment(startDate);
    const end = moment(endDate);
    const daysDifference = end.diff(start, 'days');

    if (daysDifference > 31) {
      import('sweetalert2').then((Swal) => {
        Swal.default.fire({
          icon: 'warning',
          title: 'Date Range Too Large',
          text: 'Please select a date range of maximum 31 days (1 month).',
          confirmButtonText: 'OK'
        });
      });
      return false;
    }

    if (daysDifference < 0) {
      import('sweetalert2').then((Swal) => {
        Swal.default.fire({
          icon: 'warning',
          title: 'Invalid Date Range',
          text: 'End date must be after start date.',
          confirmButtonText: 'OK'
        });
      });
      return false;
    }

    return true;
  }

  setQuickDateRange(range: string): void {
    // Set flag to prevent clearing active state during programmatic update
    this.isSettingQuickRange = true;

    // Set active button
    this.activeQuickDateRange = range;
    console.log('Active quick date range set to:', this.activeQuickDateRange); // Debug log

    const today = moment();
    let startDate: moment.Moment;
    let endDate: moment.Moment;

    switch (range) {
      case 'today':
        startDate = today.clone();
        endDate = today.clone();
        break;
      case 'yesterday':
        startDate = today.clone().subtract(1, 'day');
        endDate = today.clone().subtract(1, 'day');
        break;
      case 'thisWeek':
        startDate = today.clone().startOf('week');
        endDate = today.clone().endOf('week');
        break;
      case 'lastWeek':
        startDate = today.clone().subtract(1, 'week').startOf('week');
        endDate = today.clone().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        startDate = today.clone().startOf('month');
        endDate = today.clone().endOf('month');
        break;
      default:
        this.isSettingQuickRange = false;
        return;
    }

    // Update form controls with Date objects for owl-date-time
    this.form.patchValue({
      dateRangeLogsFrom: startDate.toDate(),
      dateRangeLogsTo: endDate.toDate()
    });

    // Reset flag after update
    setTimeout(() => {
      this.isSettingQuickRange = false;
    }, 50);

    // Force change detection
    this.cdr.detectChanges();
  }

  onDateInputChange(): void {
    if (!this.isSettingQuickRange) {
      this.activeQuickDateRange = '';
      this.cdr.detectChanges();
    }
  }

  private async getReadingData(type: string, startDate: Date, endDate: Date, zone?: string, employee?: string): Promise<any> {
    const accountKey = this.account?.account_key;

    if (!accountKey) {
      throw new Error('Account key not found');
    }

    let reportData;
    const isTaxable = this.account?.taxType === 'TAX';

    switch (type) {
      // case READING_TYPES.Z_READING:
      //   reportData = await this.salesReadingsService.processZReadingReport(accountKey, startDate, endDate, zone, employee);
      //   break;
      // case READING_TYPES.X_READING:
      //   // For X Reading, we always use current day regardless of selected dates
      //   reportData = await this.salesReadingsService.processXReadingReport(accountKey, zone, employee);
      //   break;
      case READING_TYPES.BIR_SUMMARY:
        reportData = await this.salesReadingsService.processDailySalesReport(accountKey, startDate, endDate, zone, employee);
        break;
      case READING_TYPES.REFUND:
        reportData = await this.salesReadingsService.processDailySalesReport(accountKey, startDate, endDate, zone, employee);
        // Filter to only show refunded transactions
        if (reportData) {
          reportData.transactions = reportData.refunded_transactions || [];
          reportData.total_transactions = reportData.transactions.length;
          // Recalculate totals for refunded only
          reportData.gross_sales = reportData.total_refunded_amount || 0;
          reportData.net_sales = reportData.gross_sales;
        }
        break;
      case READING_TYPES.CANCEL:
        reportData = await this.salesReadingsService.processDailySalesReport(accountKey, startDate, endDate, zone, employee);
        // Filter to only show cancelled transactions
        if (reportData) {
          reportData.transactions = reportData.cancelled_transactions || [];
          reportData.total_transactions = reportData.transactions.length;
          // Recalculate totals for cancelled only
          reportData.gross_sales = reportData.total_cancelled_amount || 0;
          reportData.net_sales = reportData.gross_sales;
        }
        break;
      case READING_TYPES.VOID:
        reportData = await this.salesReadingsService.processDailySalesReport(accountKey, startDate, endDate, zone, employee);
        // Void is similar to cancel - filter to voided transactions
        if (reportData) {
          reportData.transactions = reportData.cancelled_transactions || [];
          reportData.total_transactions = reportData.transactions.length;
          reportData.gross_sales = reportData.total_cancelled_amount || 0;
          reportData.net_sales = reportData.gross_sales;
        }
        break;
      case READING_TYPES.RETURN:
        reportData = await this.salesReadingsService.processDailySalesReport(accountKey, startDate, endDate, zone, employee);
        // Return is similar to refund - filter to returned/refunded transactions
        if (reportData) {
          reportData.transactions = reportData.refunded_transactions || [];
          reportData.total_transactions = reportData.transactions.length;
          reportData.gross_sales = reportData.total_refunded_amount || 0;
          reportData.net_sales = reportData.gross_sales;
        }
        break;
      case READING_TYPES.SENIOR_CITIZEN:
      case READING_TYPES.PWD:
      case READING_TYPES.NATIONAL_ATHLETES_COACHES:
      case READING_TYPES.SOLO_PARENT:
        // For discount-based reports, get all transactions and filter by discount type
        reportData = await this.salesReadingsService.processDailySalesReport(accountKey, startDate, endDate, zone, employee);

        // Filter transactions by discount type if transactions exist
        if (reportData?.transactions) {
          const discountTypeMap = {
            [READING_TYPES.SENIOR_CITIZEN]: ['senior citizen', 'senior', 'sc'],
            [READING_TYPES.PWD]: ['pwd', 'person with disability', 'disability'],
            [READING_TYPES.NATIONAL_ATHLETES_COACHES]: ['national athlete', 'athlete', 'coach', 'coaches'],
            [READING_TYPES.SOLO_PARENT]: ['solo parent', 'single parent']
          };
          const discountKeywords = discountTypeMap[type];

          // Filter transactions that match any of the discount keywords
          reportData.transactions = reportData.transactions.filter(t => {
            const discountDetails = (t.discount_details || '').toLowerCase();
            const paymentType = (t.payment_type || '').toLowerCase();

            // Check if any keyword matches in discount_details or payment_type
            return discountKeywords.some(keyword =>
              discountDetails.includes(keyword.toLowerCase()) ||
              paymentType.includes(keyword.toLowerCase())
            );
          });

          console.log(`Filtered ${reportData.transactions.length} transactions for ${READING_TYPE_NAMES[type]}`);

          // Recalculate totals based on filtered transactions
          this.recalculateReportTotals(reportData);
        }
        break;
      default:
        throw new Error('Invalid reading type selected');
    }

    // Apply tax calculations if the account is taxable
    if (isTaxable && reportData) {
      // Assuming 12% VAT for Philippine standard
      const VAT_RATE = 0.12;

      // Calculate VAT amounts by properly handling exempt vs taxable transactions
      if (reportData.transactions && reportData.transactions.length > 0) {
        let totalVatableSales = 0;
        let totalVatExemptSales = 0;
        
        // Process each transaction to determine if it's exempt or taxable
        reportData.transactions.forEach((transaction: any) => {
          const amount = transaction.amount || 0;
          const isExempt = this.isExemptTransaction(transaction);
          
          if (isExempt) {
            // Exempt transactions: full amount goes to VAT Exempt Sales
            totalVatExemptSales += amount;
          } else {
            // Taxable transactions: extract base amount (remove VAT)
            // Since amount is tax-inclusive, base = amount / 1.12
            totalVatableSales += amount / (1 + VAT_RATE);
          }
        });
        
        // Calculate VAT amount from vatable sales
        const vatAmount = totalVatableSales * VAT_RATE;
        
        // Assign calculated values
        reportData.vatable_sales = Math.round(totalVatableSales * 100) / 100;
        reportData.vat_amount = Math.round(vatAmount * 100) / 100;
        reportData.vat_exempt_sales = Math.round(totalVatExemptSales * 100) / 100;
      } else {
        // No transactions
        reportData.vatable_sales = 0;
        reportData.vat_amount = 0;
        reportData.vat_exempt_sales = 0;
      }
    } else if (reportData) {
      // For non-taxable accounts, set VAT fields to 0
      reportData.vatable_sales = 0;
      reportData.vat_amount = 0;
      reportData.vat_exempt_sales = reportData.gross_sales || 0;
    }


    return reportData;
  }



  getReadingTypeDisplayName(): string {
    return READING_TYPE_NAMES[this.activeReadingType] || 'Sales Summary Report';
  }

  getCurrentDateTime(): string {
    return moment().format('MMMM DD, YYYY at h:mm A');
  }

  getSelectedCashierName(): string {
    const selectedEmployeeId = this.form.get('employee')?.value;
    if (!selectedEmployeeId || selectedEmployeeId === 'all') {
      return 'All';
    }

    const selectedEmployee = this.employees.find(emp => emp.employee_id === selectedEmployeeId);
    if (selectedEmployee) {
      return `${selectedEmployee.first_name} ${selectedEmployee.last_name}`.trim();
    }

    return 'Unknown Employee';
  }

  getSelectedZoneName(): string {
    const selectedZoneId = this.form.get('zone')?.value;
    if (!selectedZoneId || selectedZoneId === 'all') {
      return 'All Zones';
    }

    const selectedZone = this.zones.find(zone => zone.zone_id === selectedZoneId || zone.zone_name === selectedZoneId);
    if (selectedZone) {
      return selectedZone.zone_name.toUpperCase();
    }

    return 'Unknown Zone';
  }

  formatCurrency(amount: number): string {
    return `P${amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  }

  calculateTax(amount: number): number {
    // VAT Rate is 12% in the Philippines
    const VAT_RATE = 0.12;
    // Calculate tax from the gross amount
    // Tax = Amount - (Amount / (1 + VAT_RATE))
    const tax = amount - (amount / (1 + VAT_RATE));
    return Math.round(tax * 100) / 100;
  }

  private recalculateReportTotals(reportData: any): void {
    if (!reportData?.transactions) return;

    const VAT_RATE = 0.12;

    // Calculate Gross Sales = ALL transactions (including refunded)
    // Refunded transactions represent money that WAS collected
    reportData.gross_sales = reportData.transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
    reportData.total_transactions = reportData.transactions.length;

    // Calculate tax-related fields
    if (this.account?.taxType === 'TAX') {
      reportData.vatable_sales = reportData.gross_sales / (1 + VAT_RATE);
      reportData.vat_amount = reportData.gross_sales - reportData.vatable_sales;
      reportData.vat_exempt_sales = 0;
    } else {
      reportData.vatable_sales = 0;
      reportData.vat_amount = 0;
      reportData.vat_exempt_sales = reportData.gross_sales;
    }

    // Calculate discounts
    reportData.total_discounts = reportData.transactions.reduce((sum, t) => sum + (t.discount_savings || t.discount_amount || 0), 0);

    // Calculate refunds = Sum of refund_amount where is_refunded=true
    reportData.total_refunds = reportData.transactions
      .filter(t => t.is_refunded === true)
      .reduce((sum, t) => sum + (t.refund_amount || t.amount || 0), 0);

    // Net sales = Gross - Discounts - Refunds
    reportData.net_sales = reportData.gross_sales - reportData.total_discounts - reportData.total_refunds;
    reportData.cumulative_grand_total = reportData.net_sales;

    // Round all values to 2 decimal places
    reportData.gross_sales = Math.round(reportData.gross_sales * 100) / 100;
    reportData.vatable_sales = Math.round(reportData.vatable_sales * 100) / 100;
    reportData.vat_amount = Math.round(reportData.vat_amount * 100) / 100;
    reportData.total_discounts = Math.round(reportData.total_discounts * 100) / 100;
    reportData.total_refunds = Math.round(reportData.total_refunds * 100) / 100;
    reportData.net_sales = Math.round(reportData.net_sales * 100) / 100;
    reportData.cumulative_grand_total = Math.round(reportData.cumulative_grand_total * 100) / 100;
  }

  // Tax calculation methods for individual transactions (BIR Tax-Exclusive Model)

  private isExemptTransaction(transaction: any): boolean {
    const paymentType = (transaction.payment_type || '').toLowerCase();
    const discountType = (transaction.discount_type || '').toLowerCase();

    return paymentType.includes('senior') || paymentType.includes('pwd') ||
      paymentType.includes('athlete') || paymentType.includes('coach') ||
      paymentType.includes('solo parent') || discountType.includes('senior') ||
      discountType.includes('pwd') || discountType.includes('athlete') ||
      discountType.includes('coach') || discountType.includes('solo parent');
  }

  getGrossSales(transaction: any): number {
    const amount = transaction.amount || 0;
    const VAT_RATE = 0.12;

    // If exempt, Gross Sales = full amount (no VAT extraction)
    if (this.isExemptTransaction(transaction)) {
      return Math.round(amount * 100) / 100;
    }

    // If taxable, Gross Sales = amount without VAT (net amount)
    // Since amount is tax-inclusive, extract the base: amount / 1.12
    const grossSales = amount / (1 + VAT_RATE);
    return Math.round(grossSales * 100) / 100;
  }

  getExemptSales(transaction: any): number {
    // If transaction is exempt, Exempt Sales = full amount
    if (this.isExemptTransaction(transaction)) {
      const amount = transaction.amount || 0;
      return Math.round(amount * 100) / 100;
    }
    return 0.00;
  }

  getZeroRatedSales(transaction: any): number {
    // Zero-rated sales (typically 0 unless specifically marked)
    return 0.00;
  }

  getTaxableSales(transaction: any): number {
    // Taxable Sales = Gross Sales - Exempt Sales - Zero Rated Sales
    const grossSales = this.getGrossSales(transaction);
    const exemptSales = this.getExemptSales(transaction);
    const zeroRatedSales = this.getZeroRatedSales(transaction);

    const taxableSales = grossSales - exemptSales - zeroRatedSales;
    return Math.round(taxableSales * 100) / 100;
  }

  getOutputTax(transaction: any): number {
    const VAT_RATE = 0.12;
    // Output VAT = Taxable Sales × 12%
    const taxableSales = this.getTaxableSales(transaction);
    const outputVAT = taxableSales * VAT_RATE;
    return Math.round(outputVAT * 100) / 100;
  }

  getGrossTaxableSales(transaction: any): number {
    // Gross Taxable Sales = Taxable Sales + Output VAT
    const taxableSales = this.getTaxableSales(transaction);
    const outputVAT = this.getOutputTax(transaction);
    const grossTaxableSales = taxableSales + outputVAT;
    return Math.round(grossTaxableSales * 100) / 100;
  }

  // Total calculation methods for all transactions
  getTotalGrossSales(): number {
    if (!this.salesReadingData?.transactions) return 0;
    const total = this.salesReadingData.transactions.reduce((sum, t) => sum + this.getGrossSales(t), 0);
    return Math.round(total * 100) / 100;
  }

  getTotalExemptSales(): number {
    if (!this.salesReadingData?.transactions) return 0;
    const total = this.salesReadingData.transactions.reduce((sum, t) => sum + this.getExemptSales(t), 0);
    return Math.round(total * 100) / 100;
  }

  getTotalZeroRatedSales(): number {
    if (!this.salesReadingData?.transactions) return 0;
    const total = this.salesReadingData.transactions.reduce((sum, t) => sum + this.getZeroRatedSales(t), 0);
    return Math.round(total * 100) / 100;
  }

  getTotalTaxableSales(): number {
    if (!this.salesReadingData?.transactions) return 0;
    const total = this.salesReadingData.transactions.reduce((sum, t) => sum + this.getTaxableSales(t), 0);
    return Math.round(total * 100) / 100;
  }

  getTotalOutputTax(): number {
    if (!this.salesReadingData?.transactions) return 0;
    const total = this.salesReadingData.transactions.reduce((sum, t) => sum + this.getOutputTax(t), 0);
    return Math.round(total * 100) / 100;
  }

  getTotalGrossTaxableSales(): number {
    if (!this.salesReadingData?.transactions) return 0;
    const total = this.salesReadingData.transactions.reduce((sum, t) => sum + this.getGrossTaxableSales(t), 0);
    return Math.round(total * 100) / 100;
  }

  formatAddress(address: any): string {
    if (!address) return 'Not Configured';

    const parts = [];
    if (address.street_house_building_number) parts.push(address.street_house_building_number);
    if (address.city) parts.push(address.city);
    if (address.province) parts.push(address.province);

    return parts.join(', ') || 'Not Configured';
  }

  formatDate(date: any): string {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY');
  }

  calculateDuration(timeIn: any, timeOut: any): string {
    if (!timeIn || !timeOut) return 'N/A';
    
    const start = moment(timeIn);
    const end = moment(timeOut);
    const duration = moment.duration(end.diff(start));
    
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return '< 1m';
    }
  }

  resetForm(): void {
    this.showResults = false;
    this.salesReadingData = null;
    this.activeReadingType = '';
    this.initializeForm();
  }

  exportToCSV(): void {
    if (!this.salesReadingData) return;

    // Import SweetAlert
    import('sweetalert2').then((Swal) => {
      try {
        // Show loading indicator
        Swal.default.fire({
          title: 'Generating CSV...',
          text: 'Please wait while we prepare your file.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.default.showLoading();
          }
        });

        const dateStr = moment().format('YYYY-MM-DD');
        let csvData = [];
        let fileName = '';

        // BIR Compliant header with dynamic report type
        csvData.push([`Point of Sales - ${this.getReadingTypeDisplayName()}`]);
        csvData.push(['TIN:', this.account.tin || 'Not Configured']);
        csvData.push(['Name:', this.account.account_name || 'Parkbot Management System']);
        csvData.push(['Business Address:', this.formatAddress(this.account.address)]);
        csvData.push(['Tax Type:', this.account.taxType || 'NON-TAX']);
        csvData.push(['']);

        // Report-specific information
        // if (this.activeReadingType === READING_TYPES.Z_READING || this.activeReadingType === READING_TYPES.X_READING) {
        //   csvData.push([`${this.activeReadingType} Reading No.:`, this.salesReadingData.reading_number || '']);
        //   csvData.push(['Machine Serial No.:', this.account.machine_serial || 'PARKBOT-001']);
        //   csvData.push(['MIN:', this.account.min_number || moment().format('YYYYMMDD') + '0001']);
        // } else if (this.activeReadingType === READING_TYPES.CANCELLED) {
        //   csvData.push(['Cancelled Transactions Report']);
        // } else {
        // For all report types (BIR Summary, Senior Citizen, PWD, etc.)
        csvData.push([this.getReadingTypeDisplayName()]);
        // }

        const zone = this.form.value.zone !== 'all' ? this.getZoneName(this.form.value.zone) : 'All Zones';
        const dateRange = `${moment(this.form.value.dateRangeLogsFrom).format('MM/DD/YYYY')} - ${moment(this.form.value.dateRangeLogsTo).format('MM/DD/YYYY')}`;

        csvData.push(['Date:', moment().format('MM/DD/YYYY')]);
        csvData.push(['Time of Reading:', moment().format('hh:mm A')]);
        csvData.push(['Zone:', zone]);
        csvData.push(['Cashier name:', this.getSelectedCashierName()]);
        csvData.push(['Period:', dateRange]);
        csvData.push(['']); // Empty row as separator

        switch (this.activeReadingType) {
          case READING_TYPES.CANCEL:
          case READING_TYPES.VOID:
            // Cancelled/Void Transactions Report
            csvData.push(['']);
            csvData.push(['Total Transactions:', this.salesReadingData.total_transactions?.toString() || '0']);
            csvData.push(['Total Cancelled Amount:', `P${(this.salesReadingData.total_cancelled_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['Cancellation Rate:', `${(this.salesReadingData.cancellation_rate || 0).toFixed(2)}%`]);
            csvData.push(['']);
            csvData.push(['TRANSACTION DETAILS:']);
            csvData.push(['Ticket #', 'Plate Number', 'Vehicle Type', 'Zone', 'Original Amount', 'Cancelled Amount', 'Reason', 'Cancelled By', 'Date']);

            (this.salesReadingData.transactions || []).forEach(t => {
              csvData.push([
                t.ticket_number || t.transaction_id || '',
                t.plate_number || '',
                t.vehicle_type || '',
                t.zone_name || '',
                `P${(t.original_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${(t.cancelled_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                t.cancellation_reason || '',
                t.cancelled_by || '',
                t.cancellation_date ? moment(t.cancellation_date).format('MM/DD/YYYY hh:mm A') : ''
              ]);
            });

            csvData.push(['']);
            csvData.push(['', '', '', 'Total', '', `P${(this.salesReadingData.total_cancelled_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, '', '', '']);

            fileName = `${this.activeReadingType}_transactions_report_${dateStr}.csv`;
            break;

          case READING_TYPES.REFUND:
          case READING_TYPES.RETURN:
            // Refund/Return Transactions Report
            csvData.push(['']);
            csvData.push(['Total Transactions:', this.salesReadingData.total_transactions?.toString() || '0']);
            csvData.push(['Total Refunded Amount:', `P${(this.salesReadingData.total_refunded_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['Refund Rate:', `${(this.salesReadingData.refund_rate || 0).toFixed(2)}%`]);
            csvData.push(['']);
            csvData.push(['TRANSACTION DETAILS:']);
            csvData.push(['Ticket #', 'Plate Number', 'Vehicle Type', 'Zone', 'Original Amount', 'Refund Amount', 'Reason', 'Refunded By', 'Date']);

            (this.salesReadingData.transactions || []).forEach(t => {
              csvData.push([
                t.ticket_number || t.transaction_id || '',
                t.plate_number || '',
                t.vehicle_type || '',
                t.zone_name || '',
                `P${(t.original_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${(t.refund_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                t.refund_reason || '',
                t.refunded_by || '',
                t.refund_date ? moment(t.refund_date).format('MM/DD/YYYY hh:mm A') : ''
              ]);
            });

            csvData.push(['']);
            csvData.push(['', '', '', 'Total', '', `P${(this.salesReadingData.total_refunded_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, '', '', '']);

            fileName = `${this.activeReadingType}_transactions_report_${dateStr}.csv`;
            break;

          case READING_TYPES.BIR_SUMMARY:
          case READING_TYPES.SENIOR_CITIZEN:
          case READING_TYPES.PWD:
          case READING_TYPES.NATIONAL_ATHLETES_COACHES:
          case READING_TYPES.SOLO_PARENT:
            csvData.push(['']);
            csvData.push(['Gross Sales:', `P${(this.salesReadingData.gross_sales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['VATable Sales:', `P${(this.salesReadingData.vatable_sales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['VAT Amount:', `P${(this.salesReadingData.vat_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['VAT Exempt Sales:', `P${(this.salesReadingData.vat_exempt_sales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['Zero-Rated Sales:', `P${(this.salesReadingData.zero_rated_sales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['Discounts:', `P${(this.salesReadingData.total_discounts || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['Returns:', `P${(this.salesReadingData.total_refunds || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['']);
            csvData.push(['Total Transactions:', this.salesReadingData.total_transactions?.toString() || '0']);
            csvData.push(['Cancelled Transactions:', this.salesReadingData.total_cancelled?.toString() || '0']);
            csvData.push(['']);
            csvData.push(['Grand Total:', `P${(this.salesReadingData.gross_sales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
            csvData.push(['']);

            // Cancelled transactions section
            if (this.salesReadingData.cancelled_transactions && this.salesReadingData.cancelled_transactions.length > 0) {
              csvData.push(['CANCELLED TRANSACTIONS:']);
              csvData.push(['Transaction ID', 'Plate Number', 'Cancelled Amount', 'Reason', 'Cancelled By', 'Date']);

              this.salesReadingData.cancelled_transactions.forEach(ct => {
                csvData.push([
                  ct.transaction_id || '',
                  ct.plate_number || '',
                  `P${(ct.cancelled_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  ct.cancellation_reason || '',
                  ct.cancelled_by || '',
                  moment(ct.cancellation_date).format('MM/DD/YYYY hh:mm A') || ''
                ]);
              });
              csvData.push(['']);
            }

            // Transaction details section
            csvData.push(['TRANSACTION DETAILS:']);

            // Generate CSV based on report type
            if (this.activeReadingType === READING_TYPES.BIR_SUMMARY) {
              // BIR Summary Report - all tax columns
              csvData.push(['Invoice Number', 'Plate Number', 'Vehicle Type', 'Duration', 'Gross Sales', 'Exempt Sales', 'Zero Rated Sales', 'Taxable Sales', 'Output Tax', 'Gross Taxable Sales']);

              this.salesReadingData.transactions.forEach(t => {
                const duration = this.calculateDuration(t.time_in, t.time_out);
                csvData.push([
                  t.invoice_number || '',
                  t.plate_number || '',
                  t.vehicle_type || '',
                  duration,
                  `P${this.getGrossSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getExemptSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getZeroRatedSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getTaxableSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getOutputTax(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getGrossTaxableSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
                ]);
              });

              csvData.push([
                '', '', '', '', '', 'Total',
                `P${this.getTotalGrossSales().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${this.getTotalExemptSales().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${this.getTotalZeroRatedSales().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${this.getTotalTaxableSales().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${this.getTotalOutputTax().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                `P${this.getTotalGrossTaxableSales().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
              ]);
            } else if (this.activeReadingType === READING_TYPES.SENIOR_CITIZEN) {
              // Senior Citizen Sales Report
              csvData.push(['Date', 'Name of Senior Citizen (SC)', 'OSCA ID No./ SC ID No.', 'SC TIN', 'SI/OR Number', 'Sales (Inclusive of VAT)', 'VAT Amount', 'VAT Exempt Sales', 'Discount 5%', 'Discount 20%', 'Net Sales']);

              this.salesReadingData.transactions.forEach(t => {
                const discount5 = (t.discount_percent === 5) ? `P${(t.discount_savings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : 'P0.00';
                const discount20 = (t.discount_percent === 20) ? `P${(t.discount_savings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : 'P0.00';

                csvData.push([
                  moment(t.time_in).format('MM/DD/YYYY') || '',
                  '', // Name
                  '', // OSCA ID
                  '', // SC TIN
                  t.invoice_number || '',
                  `P${(t.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getOutputTax(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getExemptSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  discount5,
                  discount20,
                  `P${(t.discounted_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
                ]);
              });
            } else if (this.activeReadingType === READING_TYPES.PWD) {
              // PWD Sales Report
              csvData.push(['Date', 'Name of Person with Disability (PWD)', 'PWD ID No.', 'PWD TIN', 'SI / OR Number', 'Sales (inclusive of VAT)', 'VAT Amount', 'VAT Exempt Sales', 'Discount 5%', 'Discount 20%', 'Net Sales']);

              this.salesReadingData.transactions.forEach(t => {
                const discount5 = (t.discount_percent === 5) ? `P${(t.discount_savings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : 'P0.00';
                const discount20 = (t.discount_percent === 20) ? `P${(t.discount_savings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : 'P0.00';

                csvData.push([
                  moment(t.time_in).format('MM/DD/YYYY') || '',
                  '', // Name
                  '', // PWD ID
                  '', // PWD TIN
                  t.invoice_number || '',
                  `P${(t.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getOutputTax(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${this.getExemptSales(t).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  discount5,
                  discount20,
                  `P${(t.discounted_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
                ]);
              });
            } else if (this.activeReadingType === READING_TYPES.NATIONAL_ATHLETES_COACHES) {
              // National Athletes and Coaches Sales Report
              csvData.push(['Date', 'Name of National Athlete/Coach', 'PNSTM ID No.', 'SI / OR Number', 'Gross Sales/Receipts', 'Sales Discount', 'Net Sales']);

              this.salesReadingData.transactions.forEach(t => {
                csvData.push([
                  moment(t.time_in).format('MM/DD/YYYY') || '',
                  '', // Name
                  '', // PNSTM ID
                  t.invoice_number || '',
                  `P${(t.original_amount || t.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${(t.discount_savings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${(t.discounted_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
                ]);
              });
            } else if (this.activeReadingType === READING_TYPES.SOLO_PARENT) {
              // Solo Parent Sales Report
              csvData.push(['Date', 'Name of Solo Parent', 'SPIC No.', 'Name of child', 'Birth Date of child', 'Age of child', 'SI / OR Number', 'Gross Sales', 'Discount', 'Net Sales']);

              this.salesReadingData.transactions.forEach(t => {
                csvData.push([
                  moment(t.time_in).format('MM/DD/YYYY') || '',
                  '', // Name
                  '', // SPIC No.
                  '', // Child name
                  '', // Birth date
                  '', // Age
                  t.invoice_number || '',
                  `P${(t.original_amount || t.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${(t.discount_savings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
                  `P${(t.discounted_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
                ]);
              });
            }

            // Dynamic file name based on report type
            const reportTypeFileName = this.activeReadingType.replace(/-/g, '_');
            fileName = `${reportTypeFileName}_Report_${dateStr}.csv`;
            break;

          // case READING_TYPES.CANCELLED:
          //   // Add cancelled transaction details header
          //   csvData.push(['Cancelled Transaction Details:']);
          //   csvData.push(['Transaction ID', 'Plate Number', 'Cancelled Amount', 'Reason', 'Cancelled By', 'Date']);

          //   // Add cancelled transactions data
          //   this.salesReadingData.cancelled_transactions.forEach(t => {
          //     csvData.push([
          //       t.transaction_id || '',
          //       t.plate_number || '',
          //       `P${(t.cancelled_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
          //       t.cancellation_reason || '',
          //       t.cancelled_by || '',
          //       moment(t.cancellation_date).format('MM/DD/YYYY hh:mm A') || ''
          //     ]);
          //   });

          //   // Add summary
          //   csvData.push(['']);
          //   csvData.push(['Summary:']);
          //   csvData.push(['Total Cancelled Transactions:', this.salesReadingData.cancelled_transactions.length.toString()]);
          //   csvData.push(['Total Cancelled Amount:', `P${(this.salesReadingData.total_cancelled_amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`]);
          //   csvData.push(['Cancellation Rate:', (this.salesReadingData.cancellation_rate || 0).toFixed(2) + '%']);

          //   fileName = `Cancelled_Transactions_${dateStr}.csv`;
          //   break;
        }

        // Convert to CSV string with proper escaping
        let csvContent = '';
        csvData.forEach(row => {
          // Properly escape CSV values
          const escapedRow = row.map(value => {
            if (value === null || value === undefined) return '';
            const str = String(value);
            // Escape quotes and wrap in quotes if needed
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return '"' + str.replace(/"/g, '""') + '"';
            }
            return str;
          });
          csvContent += escapedRow.join(',') + '\n';
        });

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Show success message
        Swal.default.fire({
          title: 'Success',
          text: 'CSV file has been downloaded successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error exporting to CSV:', error);
        Swal.default.fire({
          title: 'Error',
          text: 'Failed to export to CSV. Please try again.',
          icon: 'error'
        });
      }
    });
  }

  private getZXReadingDataForPDF(): Array<Array<string>> {
    const data = this.salesReadingData;
    return [
      ['Gross Sales:', this.formatCurrencyForPDF(data.gross_sales || 0)],
      ['VATable Sales:', this.formatCurrencyForPDF(data.vatable_sales || 0)],
      ['VAT Amount:', this.formatCurrencyForPDF(data.vat_amount || 0)],
      ['VAT Exempt Sales:', this.formatCurrencyForPDF(data.vat_exempt_sales || 0)],
      ['Zero-Rated Sales:', 'P0.00'],
      ['Discounts:', this.formatCurrencyForPDF(data.total_discounts || 0)],
      ['Returns:', this.formatCurrencyForPDF(data.total_refunds || 0)],
      ['', ''],
      ['Net Sales:', this.formatCurrencyForPDF(data.net_sales || 0)],
      ['Total Transactions:', data.total_transactions?.toString() || '0'],
      ['Cancelled Transactions:', data.total_cancelled?.toString() || '0'],
      ['', ''],
      ['Cumulative Grand Total:', this.formatCurrencyForPDF(data.cumulative_grand_total || data.net_sales || 0)]
    ];
  }

  private getZoneName(zoneId: string): string {
    if (!zoneId || zoneId === 'all') return 'All Zones';
    const zone = this.zones.find(z => z.zone_id === zoneId || z.zone_name === zoneId);
    return zone ? zone.zone_name : 'Unknown Zone';
  }

  private formatCurrencyForPDF(amount: number): string {
    const formattedNumber = new Intl.NumberFormat('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
    return `P${formattedNumber}`;
  }

  exportToPDF(): void {
    if (!this.salesReadingData) return;

    // Import required libraries
    import('sweetalert2').then((Swal) => {
      try {
        // Show loading indicator
        Swal.default.fire({
          title: 'Generating PDF...',
          text: 'Please wait while we prepare your file.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.default.showLoading();
          }
        });

        import('jspdf').then((jsPDF) => {
          import('jspdf-autotable').then(() => {
            try {
              // Create new PDF document
              const doc = new jsPDF.default('landscape', 'mm', 'a4');

              // Set default font for consistency
              doc.setFont('helvetica', 'normal');

              const dateStr = moment().format('YYYY-MM-DD');
              const zone = this.form.value.zone !== 'all' ? this.getZoneName(this.form.value.zone) : 'All Zones';
              const dateRange = `${moment(this.form.value.dateRangeLogsFrom).format('MM/DD/YYYY')} - ${moment(this.form.value.dateRangeLogsTo).format('MM/DD/YYYY')}`;

              // Function to add content after logo is loaded (or skipped)
              const addContentToPDF = (contentStartY: number = 15, logoWidth: number = 0) => {
                // Calculate text start position (after logo if present)
                const textStartX = logoWidth > 0 ? 14 + logoWidth + 5 : 14; // 5mm margin after logo

                // BIR Compliant Header - Three columns layout
                // Left column for title and company information
                const leftColumnX = 14; // Fixed left margin regardless of logo
                const middleColumnX = 150; // Start position for middle column
                const rightColumnX = 220; // Start position for right column (third column)

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.text(`Point of Sales - ${this.getReadingTypeDisplayName()}`, leftColumnX, contentStartY);

                // Company information - Left column
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.text(`TIN: ${this.account.tin || 'Not Configured'}`, leftColumnX, contentStartY + 10);
                doc.text(`Name: ${this.account.account_name || 'Parkbot Management System'}`, leftColumnX, contentStartY + 15);
                doc.text(`Address: ${this.account.address?.street_house_building_number || 'Not Configured'}`, leftColumnX, contentStartY + 20);
                doc.text(`City: ${this.account.address?.city || 'Not Configured'}`, leftColumnX, contentStartY + 25);
                doc.text(`Province: ${this.account.address?.province || 'Not Configured'}`, leftColumnX, contentStartY + 30);
                doc.text(`Tax Type: ${this.account.taxType || 'NON-TAX'}`, leftColumnX, contentStartY + 35);

                // Reading information - Left column
                let readingStartY = contentStartY + 40;
                // if (this.activeReadingType === READING_TYPES.Z_READING || this.activeReadingType === READING_TYPES.X_READING) {
                //   doc.text(`${this.activeReadingType} Reading No.: ${this.salesReadingData.reading_number || ''}`, leftColumnX, readingStartY);
                //   doc.text(`Machine Serial No.: ${this.account.machine_serial || 'PARKBOT-001'}`, leftColumnX, readingStartY + 5);
                //   doc.text(`MIN: ${this.account.min_number || moment().format('YYYYMMDD') + '0001'}`, leftColumnX, readingStartY + 10);
                // } else {
                doc.text(`Report Type: ${this.getReadingTypeDisplayName()}`, leftColumnX, readingStartY);
                doc.text(`TIN: ${this.account.tin || 'Not Configured'}`, leftColumnX, readingStartY + 5);
                doc.text(`Tax Type: ${this.account.taxType || 'NON-TAX'}`, leftColumnX, readingStartY + 10);
                // }

                // Middle column for date, time, zone, etc.
                doc.text(`Date: ${moment().format('MM/DD/YYYY')}`, middleColumnX, contentStartY + 10);
                doc.text(`Time of Reading: ${moment().format('hh:mm A')}`, middleColumnX, contentStartY + 15);
                doc.text(`Zone: ${zone}`, middleColumnX, contentStartY + 20);
                doc.text(`Cashier name: ${this.getSelectedCashierName()}`, middleColumnX, contentStartY + 25);
                doc.text(`Period: ${dateRange}`, middleColumnX, contentStartY + 30);

                // Right column (third column) for financial summary
                if (this.activeReadingType === READING_TYPES.BIR_SUMMARY || this.activeReadingType === READING_TYPES.SENIOR_CITIZEN ||
                  this.activeReadingType === READING_TYPES.PWD || this.activeReadingType === READING_TYPES.NATIONAL_ATHLETES_COACHES ||
                  this.activeReadingType === READING_TYPES.SOLO_PARENT) {
                  // Financial summary with cleaner layout
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(9);
                  doc.text('FINANCIAL SUMMARY', rightColumnX, contentStartY + 8);
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(9);
                  const lineHeight = 4.5;
                  let summaryY = contentStartY + 14;
                  
                  doc.text('Gross Sales:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.gross_sales || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('VATable Sales:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.vatable_sales || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('VAT Amount:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.vat_amount || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('VAT Exempt:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.vat_exempt_sales || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Zero-Rated:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.zero_rated_sales || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Discounts:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.total_discounts || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Returns:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.total_refunds || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  // Separator line
                  summaryY += lineHeight;
                  doc.setLineWidth(0.3);
                  doc.line(rightColumnX, summaryY, rightColumnX + 45, summaryY);
                  
                  summaryY += lineHeight + 1;
                  doc.setFont('helvetica', 'bold');
                  doc.text('Total Trans:', rightColumnX, summaryY);
                  doc.text(`${this.salesReadingData.total_transactions || 0}`, rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Cancelled:', rightColumnX, summaryY);
                  doc.text(`${this.salesReadingData.total_cancelled || 0}`, rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight + 1;
                  doc.setFontSize(10);
                  doc.text('GRAND TOTAL:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.gross_sales || 0), rightColumnX + 45, summaryY, { align: 'right' });
                } else if (this.activeReadingType === READING_TYPES.CANCEL || this.activeReadingType === READING_TYPES.VOID) {
                  // Cancel/Void summary - cleaner layout
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(9);
                  doc.text('CANCELLATION SUMMARY', rightColumnX, contentStartY + 8);
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(9);
                  const lineHeight = 4.5;
                  let summaryY = contentStartY + 14;
                  
                  doc.text('Total Transactions:', rightColumnX, summaryY);
                  doc.text(`${this.salesReadingData.total_transactions || 0}`, rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Cancelled Amount:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.total_cancelled_amount || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Cancellation Rate:', rightColumnX, summaryY);
                  doc.text(`${(this.salesReadingData.cancellation_rate || 0).toFixed(2)}%`, rightColumnX + 45, summaryY, { align: 'right' });
                } else if (this.activeReadingType === READING_TYPES.REFUND || this.activeReadingType === READING_TYPES.RETURN) {
                  // Refund/Return summary - cleaner layout
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(9);
                  doc.text('REFUND SUMMARY', rightColumnX, contentStartY + 8);
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(9);
                  const lineHeight = 4.5;
                  let summaryY = contentStartY + 14;
                  
                  doc.text('Total Transactions:', rightColumnX, summaryY);
                  doc.text(`${this.salesReadingData.total_transactions || 0}`, rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Refunded Amount:', rightColumnX, summaryY);
                  doc.text(this.formatCurrencyForPDF(this.salesReadingData.total_refunded_amount || 0), rightColumnX + 45, summaryY, { align: 'right' });
                  
                  summaryY += lineHeight;
                  doc.text('Refund Rate:', rightColumnX, summaryY);
                  doc.text(`${(this.salesReadingData.refund_rate || 0).toFixed(2)}%`, rightColumnX + 45, summaryY, { align: 'right' });
                }

                let yPosition = readingStartY + 40;
                let tableColumn: string[] = [];
                let tableRows: any[][] = [];
                let fileName = '';

                switch (this.activeReadingType) {
                  case READING_TYPES.CANCEL:
                  case READING_TYPES.VOID:
                    // Cancel/Void Transactions Report
                    doc.setFont('helvetica', 'bold');
                    doc.text(`${this.activeReadingType === READING_TYPES.VOID ? 'VOID' : 'CANCELLED'} TRANSACTIONS:`, 14, yPosition);

                    tableColumn = ['Ticket #', 'Plate Number', 'Vehicle Type', 'Zone', 'Original Amount', 'Cancelled Amount', 'Reason', 'Cancelled By', 'Date'];
                    tableRows = (this.salesReadingData.transactions || []).map(t => [
                      t.ticket_number || t.transaction_id || '',
                      t.plate_number || '',
                      t.vehicle_type || '',
                      t.zone_name || '',
                      this.formatCurrencyForPDF(t.original_amount || 0),
                      this.formatCurrencyForPDF(t.cancelled_amount || 0),
                      t.cancellation_reason || '',
                      t.cancelled_by || '',
                      t.cancellation_date ? moment(t.cancellation_date).format('MM/DD/YYYY hh:mm A') : ''
                    ]);

                    // Add total row
                    tableRows.push(['', '', '', 'Total', '', this.formatCurrencyForPDF(this.salesReadingData.total_cancelled_amount || 0), '', '', '']);

                    fileName = `${this.activeReadingType}-transactions-report-${dateStr}.pdf`;
                    break;

                  case READING_TYPES.REFUND:
                  case READING_TYPES.RETURN:
                    // Refund/Return Transactions Report
                    doc.setFont('helvetica', 'bold');
                    doc.text(`${this.activeReadingType === READING_TYPES.RETURN ? 'RETURN' : 'REFUND'} TRANSACTIONS:`, 14, yPosition);

                    tableColumn = ['Ticket #', 'Plate Number', 'Vehicle Type', 'Zone', 'Original Amount', 'Refund Amount', 'Reason', 'Refunded By', 'Date'];
                    tableRows = (this.salesReadingData.transactions || []).map(t => [
                      t.ticket_number || t.transaction_id || '',
                      t.plate_number || '',
                      t.vehicle_type || '',
                      t.zone_name || '',
                      this.formatCurrencyForPDF(t.original_amount || 0),
                      this.formatCurrencyForPDF(t.refund_amount || 0),
                      t.refund_reason || '',
                      t.refunded_by || '',
                      t.refund_date ? moment(t.refund_date).format('MM/DD/YYYY hh:mm A') : ''
                    ]);

                    // Add total row
                    tableRows.push(['', '', '', 'Total', '', this.formatCurrencyForPDF(this.salesReadingData.total_refunded_amount || 0), '', '', '']);

                    fileName = `${this.activeReadingType}-transactions-report-${dateStr}.pdf`;
                    break;

                  case READING_TYPES.BIR_SUMMARY:
                  case READING_TYPES.SENIOR_CITIZEN:
                  case READING_TYPES.PWD:
                  case READING_TYPES.NATIONAL_ATHLETES_COACHES:
                  case READING_TYPES.SOLO_PARENT:
                    // Skip adding financial data here as it's now in the third column of the header

                    // Set yPosition for continued processing
                    yPosition = readingStartY + 40;

                    // Cancelled transactions section - removed as per requirement

                    // Transaction details section
                    doc.setFont('helvetica', 'bold');
                    doc.text('TRANSACTION DETAILS:', 14, yPosition);

                    // Get all transactions
                    const transactions = this.salesReadingData.transactions || [];

                    // Generate table based on report type
                    if (this.activeReadingType === READING_TYPES.BIR_SUMMARY) {
                      // BIR Summary Report - all tax columns
                      tableColumn = ['Invoice Number', 'Plate Number', 'Vehicle Type', 'Duration', 'Gross Sales', 'Exempt Sales', 'Zero Rated Sales', 'Taxable Sales', 'Output Tax', 'Gross Taxable Sales'];

                      tableRows = transactions.map(t => {
                        const duration = this.calculateDuration(t.time_in, t.time_out);
                        return [
                          t.invoice_number || '',
                          t.plate_number || '',
                          t.vehicle_type || '',
                          duration,
                          this.formatCurrencyForPDF(this.getGrossSales(t)),
                          this.formatCurrencyForPDF(this.getExemptSales(t)),
                          this.formatCurrencyForPDF(this.getZeroRatedSales(t)),
                          this.formatCurrencyForPDF(this.getTaxableSales(t)),
                          this.formatCurrencyForPDF(this.getOutputTax(t)),
                          this.formatCurrencyForPDF(this.getGrossTaxableSales(t))
                        ];
                      });

                      tableRows.push([
                        '', '', '', 'Total',
                        this.formatCurrencyForPDF(this.getTotalGrossSales()),
                        this.formatCurrencyForPDF(this.getTotalExemptSales()),
                        this.formatCurrencyForPDF(this.getTotalZeroRatedSales()),
                        this.formatCurrencyForPDF(this.getTotalTaxableSales()),
                        this.formatCurrencyForPDF(this.getTotalOutputTax()),
                        this.formatCurrencyForPDF(this.getTotalGrossTaxableSales())
                      ]);
                    } else if (this.activeReadingType === READING_TYPES.SENIOR_CITIZEN) {
                      // Senior Citizen Sales Report
                      tableColumn = ['Date', 'Name of Senior Citizen (SC)', 'OSCA ID No./ SC ID No.', 'SC TIN', 'SI/OR Number', 'Sales (Inclusive of VAT)', 'VAT Amount', 'VAT Exempt Sales', '5%', '20%', 'Net Sales'];

                      tableRows = transactions.map(t => {
                        const discount5 = (t.discount_percent === 5) ? this.formatCurrencyForPDF(t.discount_savings || 0) : 'P0.00';
                        const discount20 = (t.discount_percent === 20) ? this.formatCurrencyForPDF(t.discount_savings || 0) : 'P0.00';

                        return [
                          moment(t.time_in).format('MM/DD/YYYY') || '',
                          '', // Name - not in transaction data
                          '', // OSCA ID - not in transaction data
                          '', // SC TIN - not in transaction data
                          t.invoice_number || '',
                          this.formatCurrencyForPDF(t.amount || 0),
                          this.formatCurrencyForPDF(this.getOutputTax(t)),
                          this.formatCurrencyForPDF(this.getExemptSales(t)),
                          discount5,
                          discount20,
                          this.formatCurrencyForPDF(t.discounted_amount || 0)
                        ];
                      });
                    } else if (this.activeReadingType === READING_TYPES.PWD) {
                      // PWD Sales Report
                      tableColumn = ['Date', 'Name of Person with Disability (PWD)', 'PWD ID No.', 'PWD TIN', 'SI / OR Number', 'Sales (inclusive of VAT)', 'VAT Amount', 'VAT Exempt Sales', '5%', '20%', 'Net Sales'];

                      tableRows = transactions.map(t => {
                        const discount5 = (t.discount_percent === 5) ? this.formatCurrencyForPDF(t.discount_savings || 0) : 'P0.00';
                        const discount20 = (t.discount_percent === 20) ? this.formatCurrencyForPDF(t.discount_savings || 0) : 'P0.00';

                        return [
                          moment(t.time_in).format('MM/DD/YYYY') || '',
                          '', // Name - not in transaction data
                          '', // PWD ID - not in transaction data
                          '', // PWD TIN - not in transaction data
                          t.invoice_number || '',
                          this.formatCurrencyForPDF(t.amount || 0),
                          this.formatCurrencyForPDF(this.getOutputTax(t)),
                          this.formatCurrencyForPDF(this.getExemptSales(t)),
                          discount5,
                          discount20,
                          this.formatCurrencyForPDF(t.discounted_amount || 0)
                        ];
                      });
                    } else if (this.activeReadingType === READING_TYPES.NATIONAL_ATHLETES_COACHES) {
                      // National Athletes and Coaches Sales Report
                      tableColumn = ['Date', 'Name of National Athlete/Coach', 'PNSTM ID No.', 'SI / OR Number', 'Gross Sales/Receipts', 'Sales Discount', 'Net Sales'];

                      tableRows = transactions.map(t => {
                        return [
                          moment(t.time_in).format('MM/DD/YYYY') || '',
                          '', // Name - not in transaction data
                          '', // PNSTM ID - not in transaction data
                          t.invoice_number || '',
                          this.formatCurrencyForPDF(t.original_amount || t.amount || 0),
                          this.formatCurrencyForPDF(t.discount_savings || 0),
                          this.formatCurrencyForPDF(t.discounted_amount || 0)
                        ];
                      });
                    } else if (this.activeReadingType === READING_TYPES.SOLO_PARENT) {
                      // Solo Parent Sales Report
                      tableColumn = ['Date', 'Name of Solo Parent', 'SPIC No.', 'Name of child', 'Birth Date of child', 'Age of child', 'SI / OR Number', 'Gross Sales', 'Discount', 'Net Sales'];

                      tableRows = transactions.map(t => {
                        return [
                          moment(t.time_in).format('MM/DD/YYYY') || '',
                          '', // Name - not in transaction data
                          '', // SPIC No. - not in transaction data
                          '', // Child name - not in transaction data
                          '', // Birth date - not in transaction data
                          '', // Age - not in transaction data
                          t.invoice_number || '',
                          this.formatCurrencyForPDF(t.original_amount || t.amount || 0),
                          this.formatCurrencyForPDF(t.discount_savings || 0),
                          this.formatCurrencyForPDF(t.discounted_amount || 0)
                        ];
                      });
                    }

                    // Dynamic file name based on report type
                    const reportTypeFile = this.activeReadingType.replace(/-/g, '-');
                    fileName = `${reportTypeFile}-report-${dateStr}.pdf`;
                    break;

                  // case READING_TYPES.CANCELLED:
                  //   // Summary data
                  //   doc.setFont('helvetica', 'normal');
                  //   doc.text(`Total Cancelled Transactions: ${this.salesReadingData.cancelled_transactions.length || 0}`, 14, yPosition);
                  //   yPosition += 5;
                  //   doc.text(`Total Cancelled Amount: ${this.formatCurrencyForPDF(this.salesReadingData.total_cancelled_amount || 0)}`, 14, yPosition);
                  //   yPosition += 5;
                  //   doc.text(`Cancellation Rate: ${(this.salesReadingData.cancellation_rate || 0).toFixed(2)}%`, 14, yPosition);

                  //   // Table data
                  //   tableColumn = ['Transaction ID', 'Plate Number', 'Cancelled Amount', 'Reason', 'Cancelled By', 'Date'];

                  //   tableRows = (this.salesReadingData.cancelled_transactions || []).map(t => [
                  //     t.transaction_id || '',
                  //     t.plate_number || '',
                  //     this.formatCurrencyForPDF(t.cancelled_amount || 0),
                  //     t.cancellation_reason || '',
                  //     t.cancelled_by || '',
                  //     moment(t.cancellation_date).format('MM/DD/YYYY hh:mm A') || ''
                  //   ]);

                  //   fileName = `cancelled-transactions-${dateStr}.pdf`;
                  //   break;
                }

                // Start table after header
                let tableStartY = yPosition + 5;

                // Create the table
                (doc as any).autoTable({
                  head: [tableColumn],
                  body: tableRows,
                  startY: tableStartY,
                  styles: {
                    font: 'helvetica',
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: 'linebreak',
                    textColor: [0, 0, 0]
                  },
                  headStyles: {
                    fillColor: [66, 66, 66],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    font: 'helvetica'
                  },
                  alternateRowStyles: {
                    fillColor: [240, 240, 240]
                  },
                  columnStyles: {
                    // Make sure all currency columns align to the right
                    6: { halign: 'right' },  // Gross Sales
                    7: { halign: 'right' },  // Exempt Sales
                    8: { halign: 'right' },  // Zero Rated Sales
                    9: { halign: 'right' },  // Taxable Sales
                    10: { halign: 'right' }, // Output Tax
                    11: { halign: 'right' }  // Gross Taxable Sales
                  },
                  margin: { top: 60 }
                });

                // Add financial summary at the bottom for BIR Summary report only
                if (this.activeReadingType === READING_TYPES.BIR_SUMMARY) {
                  const tableEndY = (doc as any).lastAutoTable.finalY + 20;
                  const amountColumnX = 250; // Position aligned with amount column

                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(10);

                  doc.text(`Gross Sales: ${this.formatCurrencyForPDF(this.salesReadingData.gross_sales || 0)}`, amountColumnX, tableEndY);
                  doc.text(`VATable Sales: ${this.formatCurrencyForPDF(this.salesReadingData.vatable_sales || 0)}`, amountColumnX, tableEndY + 5);
                  doc.text(`VAT Amount: ${this.formatCurrencyForPDF(this.salesReadingData.vat_amount || 0)}`, amountColumnX, tableEndY + 10);
                  doc.text(`VAT Exempt Sales: ${this.formatCurrencyForPDF(this.salesReadingData.vat_exempt_sales || 0)}`, amountColumnX, tableEndY + 15);
                  doc.text(`Zero-Rated Sales: ${this.formatCurrencyForPDF(this.salesReadingData.zero_rated_sales || 0)}`, amountColumnX, tableEndY + 20);
                  doc.text(`Discounts: ${this.formatCurrencyForPDF(this.salesReadingData.total_discounts || 0)}`, amountColumnX, tableEndY + 25);
                  doc.text(`Returns: ${this.formatCurrencyForPDF(this.salesReadingData.total_refunds || 0)}`, amountColumnX, tableEndY + 30);

                  doc.setFont('helvetica', 'bold');
                  doc.text(`Grand Total: ${this.formatCurrencyForPDF(this.salesReadingData.gross_sales || 0)}`, amountColumnX, tableEndY + 40);
                }

                // Save the PDF
                doc.save(fileName);

                // Show success message
                Swal.default.fire({
                  title: 'Success',
                  text: 'PDF file has been downloaded successfully.',
                  icon: 'success',
                  timer: 2000,
                  showConfirmButton: false
                });
              };

              // Try to load company logo with robust error handling
              if (this.account?.photoUrl) {
                console.log('Attempting to load logo:', this.account.photoUrl);
                this.pdfImageService.loadImageForPDF(this.account.photoUrl, doc, addContentToPDF);
              } else {
                console.log('No photoUrl available in account');
                // No logo available, add content normally
                addContentToPDF(15, 0);
              }
            } catch (error) {
              console.error('Error generating PDF:', error);
              Swal.default.fire({
                title: 'Error',
                text: 'Failed to generate PDF. Please try again.',
                icon: 'error'
              });
            }
          });
        });
      } catch (error) {
        console.error('Error exporting to PDF:', error);
        Swal.default.fire({
          title: 'Error',
          text: 'Failed to export to PDF. Please try again.',
          icon: 'error'
        });
      }
    });
  }

  /**
   * Print report using browser's native print function (optimized and fast)
   */
  printReport(): void {
    if (!this.salesReadingData) return;

    const zone = this.form.value.zone !== 'all' ? this.getZoneName(this.form.value.zone) : 'All Zones';
    const dateRange = `${moment(this.form.value.dateRangeLogsFrom).format('MM/DD/YYYY')} - ${moment(this.form.value.dateRangeLogsTo).format('MM/DD/YYYY')}`;

    // Build table HTML based on report type
    let tableHtml = '';
    const transactions = this.salesReadingData.transactions || [];

    if (this.activeReadingType === 'bir-summary') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Invoice Number</th>
              <th>Plate Number</th>
              <th>Vehicle Type</th>
              <th>Duration</th>
              <th>Gross Sales</th>
              <th>Exempt Sales</th>
              <th>Zero Rated</th>
              <th>Taxable Sales</th>
              <th>Output Tax</th>
              <th>Gross Taxable</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(t => `
              <tr>
                <td>${t.invoice_number || ''}</td>
                <td>${t.plate_number || ''}</td>
                <td>${t.vehicle_type || ''}</td>
                <td>${this.calculateDuration(t.time_in, t.time_out)}</td>
                <td class="amount">${this.formatCurrency(this.getGrossSales(t))}</td>
                <td class="amount">${this.formatCurrency(this.getExemptSales(t))}</td>
                <td class="amount">${this.formatCurrency(this.getZeroRatedSales(t))}</td>
                <td class="amount">${this.formatCurrency(this.getTaxableSales(t))}</td>
                <td class="amount">${this.formatCurrency(this.getOutputTax(t))}</td>
                <td class="amount">${this.formatCurrency(this.getGrossTaxableSales(t))}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="4"><strong>Total</strong></td>
              <td class="amount"><strong>${this.formatCurrency(this.getTotalGrossSales())}</strong></td>
              <td class="amount"><strong>${this.formatCurrency(this.getTotalExemptSales())}</strong></td>
              <td class="amount"><strong>${this.formatCurrency(this.getTotalZeroRatedSales())}</strong></td>
              <td class="amount"><strong>${this.formatCurrency(this.getTotalTaxableSales())}</strong></td>
              <td class="amount"><strong>${this.formatCurrency(this.getTotalOutputTax())}</strong></td>
              <td class="amount"><strong>${this.formatCurrency(this.getTotalGrossTaxableSales())}</strong></td>
            </tr>
          </tbody>
        </table>`;
    } else if (this.activeReadingType === 'cancel' || this.activeReadingType === 'void') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Ticket #</th>
              <th>Plate Number</th>
              <th>Vehicle Type</th>
              <th>Zone</th>
              <th>Original Amount</th>
              <th>Cancelled Amount</th>
              <th>Reason</th>
              <th>Cancelled By</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(t => `
              <tr>
                <td>${t.ticket_number || t.transaction_id || ''}</td>
                <td>${t.plate_number || ''}</td>
                <td>${t.vehicle_type || ''}</td>
                <td>${t.zone_name || ''}</td>
                <td class="amount">${this.formatCurrency(t.original_amount || 0)}</td>
                <td class="amount cancelled">${this.formatCurrency(t.cancelled_amount || 0)}</td>
                <td>${t.cancellation_reason || ''}</td>
                <td>${t.cancelled_by || ''}</td>
                <td>${t.cancellation_date ? moment(t.cancellation_date).format('MM/DD/YYYY hh:mm A') : ''}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="5"><strong>Total</strong></td>
              <td class="amount cancelled"><strong>${this.formatCurrency(this.salesReadingData.total_cancelled_amount || 0)}</strong></td>
              <td colspan="3"></td>
            </tr>
          </tbody>
        </table>`;
    } else if (this.activeReadingType === 'refund' || this.activeReadingType === 'return') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Ticket #</th>
              <th>Plate Number</th>
              <th>Vehicle Type</th>
              <th>Zone</th>
              <th>Original Amount</th>
              <th>Refund Amount</th>
              <th>Reason</th>
              <th>Refunded By</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(t => `
              <tr>
                <td>${t.ticket_number || t.transaction_id || ''}</td>
                <td>${t.plate_number || ''}</td>
                <td>${t.vehicle_type || ''}</td>
                <td>${t.zone_name || ''}</td>
                <td class="amount">${this.formatCurrency(t.original_amount || 0)}</td>
                <td class="amount refund">${this.formatCurrency(t.refund_amount || 0)}</td>
                <td>${t.refund_reason || ''}</td>
                <td>${t.refunded_by || ''}</td>
                <td>${t.refund_date ? moment(t.refund_date).format('MM/DD/YYYY hh:mm A') : ''}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="5"><strong>Total</strong></td>
              <td class="amount refund"><strong>${this.formatCurrency(this.salesReadingData.total_refunded_amount || 0)}</strong></td>
              <td colspan="3"></td>
            </tr>
          </tbody>
        </table>`;
    } else {
      // Generic table for other report types (senior-citizen, pwd, etc.)
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice #</th>
              <th>Amount</th>
              <th>VAT Amount</th>
              <th>Exempt Sales</th>
              <th>Discount</th>
              <th>Net Sales</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(t => `
              <tr>
                <td>${moment(t.time_in).format('MM/DD/YYYY') || ''}</td>
                <td>${t.invoice_number || ''}</td>
                <td class="amount">${this.formatCurrency(t.amount || 0)}</td>
                <td class="amount">${this.formatCurrency(this.getOutputTax(t))}</td>
                <td class="amount">${this.formatCurrency(this.getExemptSales(t))}</td>
                <td class="amount">${this.formatCurrency(t.discount_savings || 0)}</td>
                <td class="amount">${this.formatCurrency(t.discounted_amount || 0)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    }

    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Point of Sales - ${this.getReadingTypeDisplayName()}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 11px; padding: 15px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .header-left { flex: 1; }
          .header-right { flex: 1; text-align: right; }
          .header-center { flex: 1; text-align: center; }
          h1 { font-size: 16px; margin-bottom: 10px; }
          .company-info p, .report-info p { margin: 2px 0; }
          .summary-box { background: #f5f5f5; padding: 10px; margin: 15px 0; border-radius: 4px; }
          .summary-box h3 { font-size: 12px; margin-bottom: 8px; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
          .summary-item { padding: 5px; }
          .summary-item label { font-size: 9px; color: #666; display: block; }
          .summary-item span { font-size: 12px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f0f0f0; font-weight: bold; font-size: 10px; }
          td { font-size: 10px; }
          .amount { text-align: right; }
          .total-row { background: #f5f5f5; font-weight: bold; }
          .cancelled { color: #e53e3e; }
          .refund { color: #805ad5; }
          @media print {
            body { padding: 0; }
            @page { margin: 10mm; size: landscape; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <h1>Point of Sales - ${this.getReadingTypeDisplayName()}</h1>
            <div class="company-info">
              <p><strong>TIN:</strong> ${this.account?.tin || 'Not Configured'}</p>
              <p><strong>Name:</strong> ${this.account?.account_name || 'Parkbot Management System'}</p>
              <p><strong>Address:</strong> ${this.formatAddress(this.account?.address)}</p>
              <p><strong>Tax Type:</strong> ${this.account?.taxType || 'NON-TAX'}</p>
            </div>
          </div>
          <div class="header-right">
            <div class="report-info">
              <p><strong>Date:</strong> ${moment().format('MM/DD/YYYY')}</p>
              <p><strong>Time:</strong> ${moment().format('hh:mm A')}</p>
              <p><strong>Zone:</strong> ${zone}</p>
              <p><strong>Cashier:</strong> ${this.getSelectedCashierName()}</p>
              <p><strong>Period:</strong> ${dateRange}</p>
            </div>
          </div>
        </div>

        <div class="summary-box">
          <h3>FINANCIAL SUMMARY</h3>
          <div class="summary-grid">
            <div class="summary-item">
              <label>Gross Sales</label>
              <span>${this.formatCurrency(this.salesReadingData.gross_sales || 0)}</span>
            </div>
            <div class="summary-item">
              <label>VATable Sales</label>
              <span>${this.formatCurrency(this.salesReadingData.vatable_sales || 0)}</span>
            </div>
            <div class="summary-item">
              <label>VAT Amount</label>
              <span>${this.formatCurrency(this.salesReadingData.vat_amount || 0)}</span>
            </div>
            <div class="summary-item">
              <label>VAT Exempt</label>
              <span>${this.formatCurrency(this.salesReadingData.vat_exempt_sales || 0)}</span>
            </div>
            <div class="summary-item">
              <label>Discounts</label>
              <span>${this.formatCurrency(this.salesReadingData.total_discounts || 0)}</span>
            </div>
            <div class="summary-item">
              <label>Returns</label>
              <span>${this.formatCurrency(this.salesReadingData.total_refunds || 0)}</span>
            </div>
            <div class="summary-item">
              <label>Total Transactions</label>
              <span>${this.salesReadingData.total_transactions || 0}</span>
            </div>
            <div class="summary-item">
              <label>Grand Total</label>
              <span>${this.formatCurrency(this.salesReadingData.gross_sales || 0)}</span>
            </div>
          </div>
        </div>

        <h3>TRANSACTION DETAILS</h3>
        ${tableHtml}
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }

  /**
   * Get paginated transactions for Daily Sales Report
   */
  getPaginatedTransactions(): any[] {
    if (!this.salesReadingData?.transactions) return [];

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.salesReadingData.transactions.slice(startIndex, endIndex);
  }

  /**
   * Calculate total pages based on transactions
   */
  calculateTotalPages(): number {
    if (!this.salesReadingData?.transactions) return 1;
    return Math.ceil(this.salesReadingData.transactions.length / this.itemsPerPage);
  }

  /**
   * Update pagination when data is loaded
   */
  updatePagination(): void {
    this.currentPage = 1;
    this.totalPages = this.calculateTotalPages();
  }

  /**
   * Go to next page
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  /**
   * Go to previous page
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  /**
   * Get minimum of two numbers (for display text)
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Export sales reading data to JSON format as .txt file
   */
  exportToJSON(): void {
    if (!this.salesReadingData) return;

    // Import SweetAlert
    import('sweetalert2').then((Swal) => {
      try {
        // Show loading indicator
        Swal.default.fire({
          title: 'Generating JSON...',
          text: 'Please wait while we prepare your file.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.default.showLoading();
          }
        });

        const dateStr = moment().format('YYYY-MM-DD');
        const zone = this.form.value.zone !== 'all' ? this.getZoneName(this.form.value.zone) : 'All Zones';
        const dateRange = `${moment(this.form.value.dateRangeLogsFrom).format('MM/DD/YYYY')} - ${moment(this.form.value.dateRangeLogsTo).format('MM/DD/YYYY')}`;

        // Prepare JSON data structure
        const jsonData = {
          report_info: {
            report_type: this.getReadingTypeDisplayName(),
            generated_date: moment().format('MM/DD/YYYY'),
            generated_time: moment().format('hh:mm A'),
            zone: zone,
            cashier: this.getSelectedCashierName(),
            period: dateRange
          },
          company_info: {
            tin: this.account?.tin || 'Not Configured',
            name: this.account?.account_name || 'Parkbot Management System',
            address: this.formatAddress(this.account?.address),
            tax_type: this.account?.taxType || 'NON-TAX'
          },
          summary: {
            gross_sales: this.salesReadingData.gross_sales || 0,
            vatable_sales: this.salesReadingData.vatable_sales || 0,
            vat_amount: this.salesReadingData.vat_amount || 0,
            vat_exempt_sales: this.salesReadingData.vat_exempt_sales || 0,
            zero_rated_sales: this.salesReadingData.zero_rated_sales || 0,
            total_discounts: this.salesReadingData.total_discounts || 0,
            total_refunds: this.salesReadingData.total_refunds || 0,
            net_sales: this.salesReadingData.net_sales || 0,
            cumulative_grand_total: this.salesReadingData.cumulative_grand_total || this.salesReadingData.net_sales || 0,
            total_transactions: this.salesReadingData.total_transactions || 0,
            total_cancelled: this.salesReadingData.total_cancelled || 0
          },
          transactions: (this.salesReadingData.transactions || []).map(t => ({
            invoice_number: t.invoice_number || '',
            plate_number: t.plate_number || '',
            vehicle_type: t.vehicle_type || '',
            payment_type: t.payment_type || '',
            time_in: t.time_in || '',
            time_out: t.time_out || '',
            amount: t.amount || 0,
            gross_sales: this.getGrossSales(t),
            exempt_sales: this.getExemptSales(t),
            zero_rated_sales: this.getZeroRatedSales(t),
            taxable_sales: this.getTaxableSales(t),
            output_tax: this.getOutputTax(t),
            gross_taxable_sales: this.getGrossTaxableSales(t),
            discount_amount: t.discount_savings || 0,
            discount_percent: t.discount_percent || 0,
            discounted_amount: t.discounted_amount || 0,
            discount_details: t.discount_details || ''
          })),
          cancelled_transactions: (this.salesReadingData.cancelled_transactions || []).map(ct => ({
            transaction_id: ct.transaction_id || '',
            plate_number: ct.plate_number || '',
            cancelled_amount: ct.cancelled_amount || 0,
            cancellation_reason: ct.cancellation_reason || '',
            cancelled_by: ct.cancelled_by || '',
            cancellation_date: ct.cancellation_date ? moment(ct.cancellation_date).format('MM/DD/YYYY hh:mm A') : ''
          })),
          refunded_transactions: (this.salesReadingData.refunded_transactions || []).map(rt => ({
            transaction_id: rt.transaction_id || '',
            plate_number: rt.plate_number || '',
            refund_amount: rt.refund_amount || 0,
            refund_reason: rt.refund_reason || '',
            refunded_by: rt.refunded_by || '',
            refund_date: rt.refund_date ? moment(rt.refund_date).format('MM/DD/YYYY hh:mm A') : ''
          }))
        };

        // Convert to JSON string with formatting
        const jsonString = JSON.stringify(jsonData, null, 2);

        // Create filename
        const reportTypeFileName = this.activeReadingType.replace(/-/g, '_');
        const fileName = `${reportTypeFileName}_report_${dateStr}.txt`;

        // Create and download file
        const blob = new Blob([jsonString], { type: 'text/plain;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Show success message
        Swal.default.fire({
          title: 'Success',
          text: 'JSON file has been downloaded successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error exporting to JSON:', error);
        Swal.default.fire({
          title: 'Error',
          text: 'Failed to export to JSON. Please try again.',
          icon: 'error'
        });
      }
    });
  }

} 